/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: components\MemberRowItem.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the PBX Call Center module.
----------------------------------------------------------------*/


import { GripVertical, Trash2 } from 'lucide-react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Input } from '../../../components/ui/input';
import Toggle from '../../../components/ui/Toggle';
import SimpleTooltip from '../../../components/ui/SimpleTooltip';
import type { MemberRow } from '../types/ringGroups.types';
import { ExtensionSearchInput, type ExtensionOption } from './ExtensionSearchInput';

interface MemberRowItemProps {
    index: number;
    member: MemberRow;
    onUpdate: (index: number, field: keyof MemberRow, value: MemberRow[keyof MemberRow]) => void;
    onRemove: (index: number) => void;
    availableExtensions: ExtensionOption[];
    t: (key: string) => string;
}

export function MemberRowItem({ index, member, onUpdate, onRemove, availableExtensions, t }: MemberRowItemProps) {
    const sortableId = member.id ?? member.__tempId ?? index;

    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: sortableId });

    const style = { transform: CSS.Transform.toString(transform), transition };

    return (
        <div
            ref={setNodeRef}
            style={style}
            className={`grid grid-cols-12 gap-2 items-center rounded-md border border-border bg-card px-3 py-2 ${isDragging ? 'shadow-lg ring-2 ring-primary/40' : ''}`}
        >
            {/* Extension */}
            <div className="col-span-12 md:col-span-3 flex items-center gap-2">
                <button
                    type="button"
                    className="cursor-grab active:cursor-grabbing p-1 hover:bg-muted/60 rounded"
                    aria-label={t('ringGroups.form.members.actions.reorder')}
                    {...attributes}
                    {...listeners}
                >
                    <GripVertical className="h-4 w-4 text-muted-foreground" />
                </button>
                <div className="flex-1">
                    <span className="md:hidden text-xs font-medium text-muted-foreground">
                        {t('ringGroups.form.members.fields.extension.label')}
                    </span>
                    <ExtensionSearchInput
                        id={`member-extension-${index}`}
                        value={member.extension}
                        onChange={(value) => onUpdate(index, 'extension', value)}
                        options={availableExtensions}
                        placeholder={t('ringGroups.form.members.fields.extension.placeholder')}
                    />
                </div>
            </div>

            {/* Delay */}
            <div className="col-span-6 md:col-span-2">
                <span className="md:hidden text-xs font-medium text-muted-foreground">
                    {t('ringGroups.form.members.fields.delay.label')}
                </span>
                <Input
                    id={`member-delay-${index}`}
                    type="number"
                    min={0}
                    value={member.delay}
                    onChange={(e) => onUpdate(index, 'delay', e.target.value)}
                />
            </div>

            {/* Timeout */}
            <div className="col-span-6 md:col-span-2">
                <span className="md:hidden text-xs font-medium text-muted-foreground">
                    {t('ringGroups.form.members.fields.timeout.label')}
                </span>
                <Input
                    id={`member-timeout-${index}`}
                    type="number"
                    min={1}
                    value={member.timeout}
                    onChange={(e) => onUpdate(index, 'timeout', e.target.value)}
                />
            </div>

            {/* Confirm Prompt */}
            <div className="col-span-6 md:col-span-1 flex items-center gap-2 md:justify-center">
                <span className="md:hidden text-xs font-medium text-muted-foreground">
                    {t('ringGroups.form.members.fields.confirmPrompt.label')}
                </span>
                <Toggle
                    value={member.prompt.trim().length > 0}
                    onChange={(value) => onUpdate(index, 'prompt', value ? 'ivr/press_1_to_accept.wav' : '')}
                />
            </div>

            {/* Enabled */}
            <div className="col-span-6 md:col-span-1 flex items-center gap-2 md:justify-center">
                <span className="md:hidden text-xs font-medium text-muted-foreground">
                    {t('ringGroups.form.members.fields.enabled.label')}
                </span>
                <Toggle
                    value={member.enabled}
                    onChange={(value) => onUpdate(index, 'enabled', value)}
                />
            </div>

            {/* Description */}
            <div className="col-span-12 md:col-span-2">
                <span className="md:hidden text-xs font-medium text-muted-foreground">
                    {t('ringGroups.form.members.fields.description.label')}
                </span>
                <Input
                    id={`member-description-${index}`}
                    value={member.description}
                    onChange={(e) => onUpdate(index, 'description', e.target.value)}
                    placeholder={t('ringGroups.form.members.fields.description.placeholder')}
                />
            </div>

            {/* Remove */}
            <div className="col-span-12 md:col-span-1 flex justify-end">
                <SimpleTooltip content={t('ringGroups.form.members.actions.remove')}>
                    <button
                        type="button"
                        onClick={() => onRemove(index)}
                        aria-label={t('ringGroups.form.members.actions.remove')}
                        className="p-2 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60"
                    >
                        <Trash2 className="h-4 w-4 text-red-600 dark:text-red-400" />
                    </button>
                </SimpleTooltip>
            </div>
        </div>
    );
}
